import { isPast } from 'date-fns';
import { useMemo, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import PrimaryButton from '@components/buttons/PrimaryButton';
import ClientAvatar from '@components/ClientAvatar';
import CareProfileBasicForm from '@components/forms/CareProfileBasicForm';
import EditableSection from '@components/layout/EditableSection';
import Modal, { ModalElement } from '@components/Modal';
import BasicInfo from '@components/profile/BasicInfo';
import ExpertInternalNotes from '@components/profile/ExpertInternalNotes';
import ExpertReports from '@components/profile/ExpertReports';
import styles from '@components/profile/Profile.module.css';
import SupportTeam from '@components/profile/SupportTeam';
import LegalDocuments from '@components/trackers/LegalDocuments';
import Medications from '@components/trackers/Medications';
import Providers from '@components/trackers/Providers';
import BodyCopyBold from '@components/typography/BodyCopyBold';
import Heading2 from '@components/typography/Heading2';
import useMyClient from '@hooks/useMyClient';
import useMyExpert from '@hooks/useMyExpert';
import useOpener from '@hooks/useOpener';
import { ClientRoleRelationshipEnum, GetJourneyWithCareProfileQuery } from '@typing/Generated';
import { sortByTimestamp } from '@utils/modelUtils';
import { journeyRoute } from '@utils/routeUtils';

type Props = {
  journey: GetJourneyWithCareProfileQuery['journey'];
};

const Profile = ({ journey }: Props) => {
  const intl = useIntl();
  const myClient = useMyClient();
  const myExpert = useMyExpert();
  const basicInfoOpener = useOpener();
  const basicModalRef = useRef<ModalElement>(null);

  const myRole = journey.clientRoles.find(role => role.client.id === myClient?.id);

  const handleBasicError = () => {
    basicModalRef.current?.scrollToTop();
  };

  const nextConsultToReport = useMemo(() => {
    const consults = sortByTimestamp(
      journey.consults.filter(
        consult =>
          !consult.canceled &&
          isPast(consult.startTime) &&
          journey.initialConsultReport.consult?.id !== consult.id &&
          !consult.consultReport?.published
      ) ?? [],
      consult => consult.startTime
    ).reverse();

    if (consults.length) {
      return consults[0];
    }
  }, [journey.consults, journey.initialConsultReport.consult?.id]);

  const sortedConsultsByStartTime = sortByTimestamp(
    journey.consults.filter(consult => !consult.canceled) ?? [],
    consult => consult.startTime
  ).reverse();

  return (
    <div>
      <EditableSection
        icon={
          journey.careProfile.name ? (
            <ClientAvatar
              bold
              clientId={null}
              color="secondary500"
              firstName={journey.careProfile.name ?? ''}
              fontSize={20}
              journeyId={null}
              size={38}
            />
          ) : undefined
        }
        mediumTitle={false}
        onEdit={basicInfoOpener.open}
        smallSpacing
        titleKey={
          myRole?.relationship?.relationshipOption === ClientRoleRelationshipEnum.myself
            ? 'pages.profile.myProfile'
            : 'pages.profile.namesProfile'
        }
        titleValues={{ name: journey.careProfile.name ?? intl.formatMessage({ id: 'dictionary.unknown' }) }}
      >
        <div className={styles.hugeSpacingBottom}>
          <div className={styles.largeFlex + ' ' + styles.wrapMobile}>
            <BodyCopyBold color="neutral700">{journey.careProfile.name}</BodyCopyBold>
            {journey.careProfile.age && (
              <BodyCopyBold color="neutral700">
                <FormattedMessage id={`models.careProfile.age.${journey.careProfile.age}`} />
              </BodyCopyBold>
            )}
            {journey.careProfile.location && (
              <BodyCopyBold color="neutral700">{journey.careProfile.location.address}</BodyCopyBold>
            )}
          </div>
        </div>
      </EditableSection>
      <div className={styles.gridContainer}>
        <div className={styles.leftGrid}>
          {myExpert && (
            <div className={styles.hugeSpacingBottom}>
              <ExpertInternalNotes journey={journey} onlySynopsis />
            </div>
          )}
          <SupportTeam journey={journey} />
          <BasicInfo journey={journey} />
        </div>
        <div>
          {myClient ? (
            <div className={styles.trackers}>
              <Medications journey={journey} />
              <Providers journey={journey} />
              <LegalDocuments journey={journey} />
            </div>
          ) : (
            <div>
              <div className={styles.logSessions}>
                <Heading2>
                  <FormattedMessage id="pages.carePlan.base.expert.sessionDetails" />
                </Heading2>
                <PrimaryButton
                  disabled={!nextConsultToReport && journey.initialConsultReport.publishedAt}
                  routerLink={journeyRoute({
                    journeyId: journey.id,
                    linkAfterJourney: !journey.initialConsultReport.publishedAt
                      ? `/initial-consult-report/edit/${
                          sortedConsultsByStartTime.length > 0 ? sortedConsultsByStartTime[0].id : ''
                        }`
                      : nextConsultToReport
                      ? `/consults/${nextConsultToReport.id}/consult-report/edit`
                      : '',
                    myExpert
                  })}
                  textKey={
                    journey.initialConsultReport.publishedAt
                      ? 'pages.carePlan.base.expert.logSession'
                      : 'pages.carePlan.base.expert.logInitialSession'
                  }
                />
              </div>
              <ExpertReports journey={journey} />

              <ExpertInternalNotes journey={journey} />
            </div>
          )}
        </div>
      </div>
      <Modal
        opener={basicInfoOpener}
        ref={basicModalRef}
        title={intl.formatMessage({ id: 'models.careProfile.editBasicInfo' })}
      >
        {({ modalDisplayed }) => (
          <CareProfileBasicForm
            autofocus={modalDisplayed}
            careProfile={journey.careProfile}
            onCancel={basicInfoOpener.close}
            onError={handleBasicError}
            onSuccess={basicInfoOpener.close}
          />
        )}
      </Modal>
    </div>
  );
};

export default Profile;
